import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type { ExperimentalAppointment } from '../fixtures';
import type { VisitStatus } from '../theme/visitStatus';
import type { BlockReality } from '../types/blockState';

// In-memory edits, keyed by appointment_id. Honest about being a prototype
// affordance — when this surface promotes, the consumers swap to RTK Query
// mutations + direct field access. The hook signature stays similar.
export interface AttachedItem {
  id: string;
  title: string;
  /** Human-readable timing (e.g. "1 day before"). Derived from offset via
   *  `formatAppointmentDuration` for display. */
  timing?: string;
  /** ISO 8601 duration. Negative = before appointment, positive = after.
   *  Examples: `-P1D`, `-PT2H`, `-P7D`, `PT5M`, `PT0M` (at appt). */
  offset?: string;
}

export interface AppointmentOverride {
  notes?: string;
  telehealth?: boolean;
  provider_id?: string | null;
  visit_type?: string | null;
  cpt?: string | null;
  visit_status?: VisitStatus;
  duration?: number;
  start_time?: string;
  forms?: AttachedItem[];
  reminders?: AttachedItem[];
  /** Human-readable recurrence label, e.g. "Weekly · Mondays" or "One-time". */
  recurrence?: string | null;
  /** Lets the demo flip a live appointment into a cancelled / no-show /
   *  rescheduling state via the cancel button — flows through into
   *  `block_state.reality` for downstream variant + waitlist surfacing. */
  reality?: BlockReality;
}

interface CtxValue {
  getOverride: (id: string) => AppointmentOverride | undefined;
  setOverride: (id: string, partial: Partial<AppointmentOverride>) => void;
  clearOverride: (id: string) => void;
}

const Ctx = createContext<CtxValue | null>(null);

export const AppointmentOverridesProvider = ({ children }: { children: ReactNode }) => {
  const [overrides, setOverrides] = useState<Record<string, AppointmentOverride>>({});

  const getOverride = useCallback(
    (id: string): AppointmentOverride | undefined => overrides[id],
    [overrides],
  );

  const setOverride = useCallback((id: string, partial: Partial<AppointmentOverride>) => {
    setOverrides((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...partial },
    }));
  }, []);

  const clearOverride = useCallback((id: string) => {
    setOverrides((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const value = useMemo(
    () => ({ getOverride, setOverride, clearOverride }),
    [getOverride, setOverride, clearOverride],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAppointmentOverrides = (): CtxValue => {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useAppointmentOverrides must be used inside AppointmentOverridesProvider');
  }
  return ctx;
};

/**
 * Pure merge — fold an override onto an appointment without touching React.
 * Used by useMergedAppointment for per-event reads, and by CalendarWidget
 * for the events array (so drag/resize moves are reflected on the grid).
 * The original fixture record is never mutated; telehealth is synced into
 * both top-level and block_state.qualifiers.
 */
export const mergeAppointmentOverride = (
  appt: ExperimentalAppointment,
  override: AppointmentOverride | undefined,
): ExperimentalAppointment => {
  if (!override) return appt;
  let merged: ExperimentalAppointment = { ...appt };
  if (override.notes !== undefined) merged = { ...merged, notes: override.notes };
  if (override.provider_id !== undefined) merged = { ...merged, provider_id: override.provider_id };
  if (override.visit_type !== undefined) merged = { ...merged, visit_type: override.visit_type };
  if (override.cpt !== undefined) merged = { ...merged, cpt: override.cpt };
  if (override.visit_status !== undefined) merged = { ...merged, visit_status: override.visit_status };
  if (override.duration !== undefined) merged = { ...merged, duration: override.duration };
  if (override.start_time !== undefined) merged = { ...merged, start_time: override.start_time };
  if (override.telehealth !== undefined) {
    merged = { ...merged, telehealth: override.telehealth };
    if (merged.block_state) {
      merged = {
        ...merged,
        block_state: {
          ...merged.block_state,
          qualifiers: {
            ...merged.block_state.qualifiers,
            telehealth: override.telehealth,
          },
        },
      };
    }
  }
  if (override.reality !== undefined && merged.block_state) {
    merged = {
      ...merged,
      block_state: { ...merged.block_state, reality: override.reality },
    };
  }
  return merged;
};

/**
 * Merge in-flight overrides into an appointment for read paths.
 */
export const useMergedAppointment = (appt: ExperimentalAppointment): ExperimentalAppointment => {
  const { getOverride } = useAppointmentOverrides();
  const override = getOverride(appt.appointment_id);
  return useMemo(() => mergeAppointmentOverride(appt, override), [appt, override]);
};
